import React, { useState, useMemo } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// CONFIG - Minnesota DOT Specification 2399 (2025)
// =============================================================================

const EQUATIONS = {
  'HMA-A': { 
    id: 'HMA-A', 
    label: 'HMA-A',
    description: '3+ lift new construction',
    maxIncentive: 400,
    threshold1: 25.0,
    threshold2: 75.0,
    slope: 16,
    intercept: 800,
    correctiveTo: 50.0,
    zeroPoint: 50.0,
  },
  'HMA-B': { 
    id: 'HMA-B', 
    label: 'HMA-B',
    description: '2-lift construction',
    maxIncentive: 270,
    threshold1: 30.0,
    threshold2: 80.0,
    slope: 10.8,
    intercept: 594,
    correctiveTo: 55.0,
    zeroPoint: 55.0,
  },
  'HMA-C': { 
    id: 'HMA-C', 
    label: 'HMA-C',
    description: 'Single lift overlay',
    maxIncentive: 180,
    threshold1: 35.0,
    threshold2: 95.0,
    slope: 6,
    intercept: 390,
    correctiveTo: 65.0,
    zeroPoint: 65.0,
  },
  'PCC-A': { 
    id: 'PCC-A', 
    label: 'PCC-A',
    description: 'Concrete pavement',
    maxIncentive: 890,
    threshold1: 45.0,
    threshold2: 85.0,
    slope: 44.5,
    intercept: 2892.5,
    correctiveTo: 65.0,
    zeroPoint: 65.0,
  },
};

const SPEED_TYPES = {
  HIGH: { id: 'HIGH', label: '> 45 mph' },
  LOW: { id: 'LOW', label: '> 35 mph and ≤ 45 mph' },
};

const LANE_TYPES = {
  MAINLINE: { id: 'MAINLINE', label: 'Mainline', evaluateSmoothness: true },
  ACCEL_LONG: { id: 'ACCEL_LONG', label: 'Accel/decel ≥ 1,000 ft', evaluateSmoothness: true },
  ACCEL_SHORT: { id: 'ACCEL_SHORT', label: 'Accel/decel < 1,000 ft', evaluateSmoothness: false },
  RAMP: { id: 'RAMP', label: 'Ramp or loop', evaluateSmoothness: false },
};

const DENSITY_STATUS = {
  PASS: { id: 'PASS', label: 'Failure Rate: ≤ 25%' },
  FAIL: { id: 'FAIL', label: 'Failure Rate: > 25%' },
};

const DEFAULT_SETTINGS = {
  equation: 'HMA-A',
  speedType: 'HIGH',
  laneType: 'MAINLINE',
  densityStatus: 'PASS',
  excluded: false,
};

const SEGMENT_LENGTH = 528; // 0.1 mile in feet

// =============================================================================
// CALCULATION HELPERS
// =============================================================================

const getMRIAdjustment = (mri, equation, segmentLength = SEGMENT_LENGTH) => {
  const eq = EQUATIONS[equation];
  const lengthFactor = segmentLength / SEGMENT_LENGTH;
  const mriRounded = Math.round(mri * 10) / 10;
  
  if (mriRounded < eq.threshold1) {
    return {
      adjustment: eq.maxIncentive * lengthFactor,
      tier: 'bonus',
      grind: false,
      grindTarget: null,
    };
  } else if (mriRounded <= eq.threshold2) {
    const basePay = eq.intercept - (eq.slope * mriRounded);
    return {
      adjustment: basePay * lengthFactor,
      tier: basePay >= 0 ? 'bonus' : 'penalty',
      grind: false,
      grindTarget: null,
    };
  } else {
    // Corrective work required
    // If segment < 100 ft, prorate max disincentive instead
    if (segmentLength < 100) {
      const maxDisincentive = eq.intercept - (eq.slope * eq.threshold2);
      return {
        adjustment: maxDisincentive * lengthFactor,
        tier: 'penalty',
        grind: false,
        grindTarget: null,
      };
    }
    return {
      adjustment: 0,
      tier: 'corrective',
      grind: true,
      grindTarget: eq.correctiveTo,
    };
  }
};

const getALRAdjustment = (alrMagnitude, alrLengthFt, settings) => {
  const { equation, speedType, laneType } = settings;
  const lane = LANE_TYPES[laneType];
  
  const isHighThreshold = 
    (equation === 'HMA-A' || equation === 'HMA-B' || equation === 'PCC-A') && 
    speedType === 'HIGH' && 
    lane.evaluateSmoothness;
  
  if (isHighThreshold) {
    // Table 2399-7: HMA-A, HMA-B, PCC-A with speed > 45 mph
    if (alrMagnitude < 175) {
      return { adjustment: 0, penaltyPerFoot: 0, tier: 'ok', grind: false };
    } else if (alrMagnitude < 225) {
      // 175-224.9: $25/ft or correct by mutual agreement
      return { 
        adjustment: -(25 * alrLengthFt), 
        penaltyPerFoot: 25, 
        tier: 'penalty',
        grind: false,
        grindOption: true,
        grindTarget: 175,
      };
    } else {
      // ≥225: $125/ft (HMA) or must correct (PCC)
      if (equation === 'PCC-A') {
        return { adjustment: 0, penaltyPerFoot: 0, tier: 'corrective', grind: true, grindTarget: 175 };
      } else {
        return { 
          adjustment: -(125 * alrLengthFt), 
          penaltyPerFoot: 125, 
          tier: 'penalty',
          grind: false,
        };
      }
    }
  } else {
    // HMA-C, ramps, loops, or speed ≤ 45 mph - only ≥225 threshold
    if (alrMagnitude < 225) {
      return { adjustment: 0, penaltyPerFoot: 0, tier: 'ok', grind: false };
    } else {
      return { 
        adjustment: -(25 * alrLengthFt), 
        penaltyPerFoot: 25, 
        tier: 'penalty',
        grind: false,
      };
    }
  }
};

const evaluatesSmoothness = (settings) => {
  const lane = LANE_TYPES[settings.laneType];
  return lane.evaluateSmoothness && settings.speedType === 'HIGH';
};

// =============================================================================
// MAIN CALCULATION
// =============================================================================

const calculate = (segments, alrDefects, settings, isReversed) => {
  if (!segments?.length) return null;
  
  const validSegments = segments
    .filter(seg => !seg.exception)
    .map((seg) => ({
      ...seg,
      startStation: seg.from_station_ft || 0,
      stopStation: seg.to_station_ft || 0,
    }));
  
  if (validSegments.length === 0) return null;
  
  const sortedSegments = [...validSegments].sort((a, b) => 
    isReversed 
      ? b.startStation - a.startStation
      : a.startStation - b.startStation
  );
  
  let totalMRIAdjustment = 0;
  let totalDistance = 0;
  let mriSum = 0;
  let correctiveCount = 0;
  
  const canEvaluateSmoothness = evaluatesSmoothness(settings);
  
  const rows = sortedSegments.map((seg, i) => {
    const mri = seg.iri_avg_in_per_mi || 0;
    const segmentLength = Math.abs(seg.stopStation - seg.startStation);
    
    let mriResult;
    if (!canEvaluateSmoothness) {
      mriResult = { adjustment: 0, tier: 'excluded', grind: false, grindTarget: null };
    } else {
      mriResult = getMRIAdjustment(mri, settings.equation, segmentLength);
    }
    
    if (!mriResult.grind) {
      totalMRIAdjustment += mriResult.adjustment;
    }
    totalDistance += segmentLength;
    mriSum += mri;
    if (mriResult.grind) correctiveCount++;
    
    const displayStart = isReversed ? seg.stopStation : seg.startStation;
    const displayStop = isReversed ? seg.startStation : seg.stopStation;
    
    return {
      num: i + 1,
      segmentId: seg.segment_id,
      mri,
      startStation: displayStart,
      stopStation: displayStop,
      length: segmentLength,
      adjustment: mriResult.adjustment,
      tier: mriResult.tier,
      requiresGrind: mriResult.grind,
      grindTarget: mriResult.grindTarget,
      hasIssue: mriResult.tier === 'corrective' || mriResult.tier === 'penalty',
    };
  });
  
  // Apply density failure: zero out positive pay
  if (settings.densityStatus === 'FAIL' && totalMRIAdjustment > 0) {
    totalMRIAdjustment = 0;
  }
  
  // Calculate ALR totals from defects (like Iowa)
  let alrLengthMid = 0;      // 175-225 band
  let alrLengthHigh = 0;     // ≥225 band
  let alrLengthCorrective = 0;
  let totalALRDeduction = 0;
  
  (alrDefects || []).forEach(defect => {
    const magnitude = defect.magnitude || 0;
    const lengthFt = defect.length || Math.abs((defect.stopDistance || 0) - (defect.startDistance || 0));
    const alrResult = getALRAdjustment(magnitude, lengthFt, settings);
    
    if (alrResult.tier === 'penalty') {
      // Track which band it falls into for display
      if (magnitude >= 175 && magnitude < 225) {
        alrLengthMid += lengthFt;
      } else if (magnitude >= 225) {
        alrLengthHigh += lengthFt;
      }
      totalALRDeduction += alrResult.adjustment;
    }
    if (alrResult.tier === 'corrective') {
      alrLengthCorrective += lengthFt;
    }
  });
  
  // Apply exclusion
  if (settings.excluded) {
    totalALRDeduction = 0;
    alrLengthMid = 0;
    alrLengthHigh = 0;
    alrLengthCorrective = 0;
  }
  
  const avgMRI = rows.length > 0 ? mriSum / rows.length : 0;
  
  // Banker's rounding for combined total
  const combinedRaw = totalMRIAdjustment + totalALRDeduction;
  const combinedTotal = Math.round(combinedRaw * 100) / 100;
  
  return {
    rows,
    totalMRIAdjustment,
    totalALRDeduction,
    combinedTotal,
    avgMRI,
    totalDistance,
    segmentCount: rows.length,
    correctiveCount,
    alrLengthMid,
    alrLengthHigh,
    alrLengthCorrective,
  };
};

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');
const fmtStation = (ft) => {
  if (ft == null) return '—';
  const hundreds = Math.floor(ft / 100);
  const remainder = Math.round(ft % 100);
  return `${hundreds}+${String(remainder).padStart(2, '0')}`;
};
const fmtCurrency = (v) => {
  const formatted = Math.abs(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  return v < 0 ? `-${formatted}` : formatted;
};

// =============================================================================
// COPY VALUE COMPONENT
// =============================================================================

const CopyValue = ({ label, value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className={cn('flex items-center justify-between py-1.5', className)}>
      <span className="text-gray-600 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold text-gray-900">{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// COPYABLE CELL COMPONENT
// =============================================================================

const CopyableCell = ({ value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <td className={cn('px-3 py-2 group', className)}>
      <div className="flex items-center gap-1">
        <span>{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-3 w-3 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </td>
  );
};

// =============================================================================
// COPYABLE COLUMN HEADER
// =============================================================================

const CopyableHeader = ({ label, data, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(data.join('\r\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <th className={cn('px-3 py-2 text-left group', className)}>
      <span>{label}</span>
      <button
        onClick={handleCopy}
        className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title={`Copy ${label} column`}
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5 text-green-500 inline" />
        ) : (
          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 inline" />
        )}
      </button>
    </th>
  );
};

// =============================================================================
// PAY TABLE MODAL
// =============================================================================

const PayTableModal = ({ open, onClose, settings }) => {
  const eq = EQUATIONS[settings.equation];
  const isHighThreshold = 
    (settings.equation === 'HMA-A' || settings.equation === 'HMA-B' || settings.equation === 'PCC-A') && 
    settings.speedType === 'HIGH';
  
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <DialogPanel className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <DialogTitle className="text-base font-semibold text-gray-900">
              MnDOT Spec 2399 Pay Adjustment Tables
            </DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-6">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="font-semibold text-blue-800 mb-1">Current Configuration</div>
              <div className="text-blue-700">
                {eq.label} ({eq.description}) • {SPEED_TYPES[settings.speedType].label} • {LANE_TYPES[settings.laneType].label}
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">
                {eq.label} Smoothness Pay Adjustment — Per 0.1 Mile Segment
              </h3>
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">MRI Range (in/mi)</th>
                    <th className="px-3 py-2 text-left border-b">Formula</th>
                    <th className="px-3 py-2 text-left border-b">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr>
                    <td className="px-3 py-2 text-gray-700">&lt; {eq.threshold1}</td>
                    <td className="px-3 py-2 font-mono text-green-700">${eq.maxIncentive.toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">Max incentive</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2 text-gray-700">{eq.threshold1} – {eq.threshold2}</td>
                    <td className="px-3 py-2 font-mono text-gray-700">${eq.intercept} − {eq.slope} × MRI</td>
                    <td className="px-3 py-2 text-xs text-gray-600">Zero at MRI = {eq.zeroPoint}</td>
                  </tr>
                  <tr className="bg-red-50">
                    <td className="px-3 py-2 text-gray-700">&gt; {eq.threshold2}</td>
                    <td className="px-3 py-2 font-mono text-red-700">Corrective Work</td>
                    <td className="px-3 py-2 text-xs text-red-600 font-semibold">Grind to ≤ {eq.correctiveTo}</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-2 text-xs text-gray-500">
                Segments &lt; 100 ft receive prorated disincentive instead of corrective work requirement.
              </p>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">ALR Deduction — Per Linear Foot</h3>
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">ALR Range (in/mi)</th>
                    <th className="px-3 py-2 text-left border-b">$/foot</th>
                    <th className="px-3 py-2 text-left border-b">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {isHighThreshold ? (
                    <>
                      <tr>
                        <td className="px-3 py-2 text-gray-700">&lt; 175.0</td>
                        <td className="px-3 py-2 font-mono text-gray-400">—</td>
                        <td className="px-3 py-2 text-xs text-gray-600">Acceptable</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 text-gray-700">175.0 – 224.9</td>
                        <td className="px-3 py-2 font-mono text-red-700">-$25.00</td>
                        <td className="px-3 py-2 text-xs text-gray-600">Pay or correct (mutual agreement)</td>
                      </tr>
                      <tr className={settings.equation === 'PCC-A' ? 'bg-red-50' : ''}>
                        <td className="px-3 py-2 text-gray-700">≥ 225.0</td>
                        <td className="px-3 py-2 font-mono text-red-700">
                          {settings.equation === 'PCC-A' ? 'Correct' : '-$125.00'}
                        </td>
                        <td className="px-3 py-2 text-xs text-red-600 font-semibold">
                          {settings.equation === 'PCC-A' ? 'Must correct' : 'Deduction (HMA)'}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr>
                        <td className="px-3 py-2 text-gray-700">&lt; 225.0</td>
                        <td className="px-3 py-2 font-mono text-gray-400">—</td>
                        <td className="px-3 py-2 text-xs text-gray-600">Acceptable</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 text-gray-700">≥ 225.0</td>
                        <td className="px-3 py-2 font-mono text-red-700">-$25.00</td>
                        <td className="px-3 py-2 text-xs text-gray-600">Deduction only</td>
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
            
            <div className="p-3 bg-gray-50 rounded text-xs text-gray-600">
              <p className="font-semibold mb-1">Reference</p>
              <p>MnDOT Standard Specifications 2399 — Pavement Surface Smoothness (2025)</p>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function MinnesotaPayAdjustment({ project, segments, alrDefects, isReversed = false }) {
  const [settings, setSettings] = useState(() => ({ 
    ...DEFAULT_SETTINGS, 
    ...project?.payAdjustment,
  }));
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  
  const data = useMemo(() => calculate(segments, alrDefects, settings, isReversed), [segments, alrDefects, settings, isReversed]);
  
  const displayRows = useMemo(() => {
    if (!data) return [];
    return issuesOnly ? data.rows.filter(r => r.hasIssue) : data.rows;
  }, [data, issuesOnly]);
  
  const eq = EQUATIONS[settings.equation];
  const canEvaluateSmoothness = evaluatesSmoothness(settings);
  
  // ALR threshold labels based on settings
  const isHighALRThreshold = 
    (settings.equation === 'HMA-A' || settings.equation === 'HMA-B' || settings.equation === 'PCC-A') && 
    settings.speedType === 'HIGH' &&
    LANE_TYPES[settings.laneType].evaluateSmoothness;
  
  const alrMidLabel = '175.0 ≤ ALR < 225.0 (ft.)';
  const alrHighLabel = 'ALR ≥ 225.0 (ft.)';
  
  const exportCSV = () => {
    const headers = ['Segment', 'Beginning Sta.', 'Ending Sta.', 'Segment Length (ft)', 'Final Smoothness (in/mi)', 'Segment Pay Adjustment'].join(',');
    const csvRows = displayRows.map(r => [
      r.num,
      r.startStation.toFixed(0),
      r.stopStation.toFixed(0),
      r.length.toFixed(0),
      r.mri.toFixed(1),
      r.requiresGrind ? 'CW' : r.adjustment.toFixed(2),
    ].join(','));
    const blob = new Blob([[headers, ...csvRows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `mndot-2399-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  
  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <PayTableModal open={showPayTable} onClose={() => setShowPayTable(false)} settings={settings} />
      
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="mr-2">
          <h3 className="text-sm font-semibold text-gray-900">Profile Summary Sheet</h3>
          <p className="text-xs text-gray-500">MnDOT Specification 2399</p>
        </div>
        
        <div className="h-8 w-px bg-gray-300" />
        
        {/* Equation */}
        <Listbox value={settings.equation} onChange={(v) => setSettings(s => ({ ...s, equation: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              {EQUATIONS[settings.equation]?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(EQUATIONS).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}>
                  <span className="font-medium">{t.label}</span>
                  <span className="text-gray-500 ml-2 text-xs">{t.description}</span>
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        {/* Lane Type */}
        <Listbox value={settings.laneType} onChange={(v) => setSettings(s => ({ ...s, laneType: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              {LANE_TYPES[settings.laneType]?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(LANE_TYPES).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}>
                  {t.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        {/* Speed */}
        <Listbox value={settings.speedType} onChange={(v) => setSettings(s => ({ ...s, speedType: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              {SPEED_TYPES[settings.speedType]?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(SPEED_TYPES).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}>
                  {t.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        {/* Density Status */}
        <Listbox value={settings.densityStatus} onChange={(v) => setSettings(s => ({ ...s, densityStatus: v }))}>
          <div className="relative">
            <ListboxButton className={cn(
              "h-8 pl-3 pr-7 text-sm border rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap",
              settings.densityStatus === 'FAIL' ? 'bg-orange-50 border-orange-300 text-orange-800' : 'bg-white border-gray-300'
            )}>
              {DENSITY_STATUS[settings.densityStatus]?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(DENSITY_STATUS).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}>
                  {t.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        <div className="flex-1" />
        
        <button 
          onClick={() => setShowPayTable(true)} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Tables
        </button>
        
        <button 
          onClick={exportCSV} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>
      
      {/* Summary for DOT Sheet */}
      {data ? (
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-6">
            {/* Left: ALR Summary */}
            <div className="border border-gray-200 rounded">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700">Areas of Localized Roughness (ALR)</h4>
                <Listbox value={settings.excluded ? 'YES' : 'NO'} onChange={(v) => setSettings(s => ({ ...s, excluded: v === 'YES' }))}>
                  <div className="relative">
                    <ListboxButton className={cn(
                      'h-7 pl-2 pr-6 text-xs font-medium border rounded',
                      settings.excluded 
                        ? 'bg-yellow-100 border-yellow-300 text-yellow-800' 
                        : 'bg-white border-gray-300 text-gray-700'
                    )}>
                      {settings.excluded ? 'EXCLUDED' : 'NOT EXCLUDED'}
                      <ChevronUpDownIcon className="absolute right-1 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </ListboxButton>
                    <ListboxOptions className="absolute right-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
                      <ListboxOption value="NO" className={({ active }) => cn('px-3 py-1.5 cursor-pointer text-xs', active && 'bg-blue-50')}>
                        NOT EXCLUDED
                      </ListboxOption>
                      <ListboxOption value="YES" className={({ active }) => cn('px-3 py-1.5 cursor-pointer text-xs', active && 'bg-blue-50')}>
                        EXCLUDED
                      </ListboxOption>
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>
              <div className="px-3 py-2 divide-y divide-gray-100">
                {isHighALRThreshold && (
                  <CopyValue 
                    label={alrMidLabel}
                    value={settings.excluded ? '0.00' : data.alrLengthMid.toFixed(2)}
                  />
                )}
                <CopyValue 
                  label={alrHighLabel}
                  value={settings.excluded ? '0.00' : (data.alrLengthHigh + data.alrLengthCorrective).toFixed(2)}
                />
              </div>
            </div>
            
            {/* Right: Totals */}
            {(() => {
              const correctionRequired = !settings.excluded && (data.correctiveCount > 0 || data.alrLengthCorrective > 0);
              
              return (
                <div className="border border-gray-200 rounded">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-700">Totals</h4>
                    {correctionRequired && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded">
                        Correction Required
                      </span>
                    )}
                    {!canEvaluateSmoothness && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-700 rounded">
                        Excluded from Smoothness
                      </span>
                    )}
                  </div>
                  <div className="px-3 py-2 divide-y divide-gray-100">
                    {data.correctiveCount > 0 ? (
                      <div className="flex items-center justify-between py-1.5">
                        <span className="text-gray-600 text-sm">Total Pay Adjustment</span>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                            Corrective Work Needed
                          </span>
                          <span className="font-mono text-sm text-gray-400">
                            {fmtCurrency(data.totalMRIAdjustment)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <CopyValue 
                        label="Total Pay Adjustment"
                        value={data.totalMRIAdjustment.toFixed(2)}
                        displayValue={fmtCurrency(data.totalMRIAdjustment)}
                      />
                    )}
                    {data.alrLengthCorrective > 0 ? (
                      <div className="flex items-center justify-between py-1.5">
                        <span className="text-gray-600 text-sm">Areas of Localized Roughness Deduction</span>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                            Corrective Work Needed
                          </span>
                          <span className="font-mono text-sm text-gray-400">
                            {settings.excluded ? '$0.00' : fmtCurrency(data.totalALRDeduction)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <CopyValue 
                        label="Areas of Localized Roughness Deduction"
                        value={settings.excluded ? '0.00' : data.totalALRDeduction.toFixed(2)}
                        displayValue={settings.excluded ? '$0.00' : fmtCurrency(data.totalALRDeduction)}
                      />
                    )}
                    <div className="flex items-center justify-between py-1.5 font-semibold">
                      <span className="text-gray-600 text-sm">Combined Total</span>
                      {correctionRequired ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">—</span>
                          <span className="font-mono text-sm text-gray-400 font-normal">
                            {settings.excluded ? fmtCurrency(data.totalMRIAdjustment) : fmtCurrency(data.combinedTotal)}
                          </span>
                        </div>
                      ) : (
                        <span className="font-mono text-sm text-gray-900">
                          {settings.excluded ? fmtCurrency(data.totalMRIAdjustment) : fmtCurrency(data.combinedTotal)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">
          No segment data available
        </div>
      )}
      
      {/* Segments Header */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Segments {data && <span className="text-gray-400 font-normal">({data.rows.length})</span>}
        </span>
        {data && data.correctiveCount > 0 && (
          <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={issuesOnly} 
              onChange={e => setIssuesOnly(e.target.checked)} 
              className="w-3.5 h-3.5 text-blue-600 rounded" 
            />
            Show issues only
          </label>
        )}
      </div>
      
      {/* Table */}
      <div className="flex-1 overflow-auto">
        {displayRows.length > 0 ? (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left w-12"></th>
                <th className="px-3 py-2 text-left">Beginning Sta.</th>
                <th className="px-3 py-2 text-left">Ending Sta.</th>
                <th className="px-3 py-2 text-left">Segment Length (ft)</th>
                <CopyableHeader label="Final Smoothness (in/mi)" data={displayRows.map(r => r.mri.toFixed(1))} />
                <CopyableHeader label="Segment Pay Adjustment" data={displayRows.map(r => r.requiresGrind ? 'CW' : r.adjustment.toFixed(2))} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.map(r => (
                <tr key={r.num} className={cn('hover:bg-gray-50', r.requiresGrind && 'bg-red-50')}>
                  <td className="px-3 py-2 text-gray-400 text-xs">{r.num}</td>
                  <CopyableCell 
                    value={r.startStation.toFixed(0)} 
                    displayValue={fmtStation(r.startStation)}
                    className="text-gray-700 font-mono text-xs"
                  />
                  <CopyableCell 
                    value={r.stopStation.toFixed(0)} 
                    displayValue={fmtStation(r.stopStation)}
                    className="text-gray-700 font-mono text-xs"
                  />
                  <CopyableCell 
                    value={r.length.toFixed(0)} 
                    displayValue={r.length.toFixed(0)}
                    className="text-gray-700"
                  />
                  <td className={cn('px-3 py-2 text-left', r.requiresGrind ? 'text-red-600 font-semibold' : 'text-gray-900')}>
                    {r.mri.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-left">
                    {r.requiresGrind ? (
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                          CW
                        </span>
                        <span className="font-mono text-gray-400 text-xs">
                          → ≤{r.grindTarget}
                        </span>
                      </div>
                    ) : (
                      <span className={cn(
                        'font-mono',
                        r.adjustment > 0 ? 'text-green-600' : r.adjustment < 0 ? 'text-red-600' : 'text-gray-500'
                      )}>
                        {fmtCurrency(r.adjustment)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-xs text-gray-500 text-right">Total Distance:</td>
                <td className="px-3 py-2 font-semibold text-gray-900">{data.totalDistance.toFixed(0)}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  <span className="mr-2">Avg. MRI:</span>
                  <span className="font-semibold text-gray-900">{data.avgMRI.toFixed(1)}</span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {(data.correctiveCount > 0 || data.alrLengthCorrective > 0) && (
                    <span className="text-red-600 font-medium">
                      Corrections: {data.correctiveCount > 0 && `${data.correctiveCount} segment${data.correctiveCount !== 1 ? 's' : ''}`}
                      {data.correctiveCount > 0 && data.alrLengthCorrective > 0 && ', '}
                      {data.alrLengthCorrective > 0 && `${data.alrLengthCorrective.toFixed(1)} ft ALR`}
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
            <p>{issuesOnly ? 'No segments with issues' : 'No segment data available'}</p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span>MnDOT Specification 2399 (2025)</span>
          {isReversed && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Reverse Stationing</span>
          )}
          {settings.densityStatus === 'FAIL' && (
            <span className="px-2 py-0.5 bg-orange-100 text-orange-700 rounded">Density Penalty Applied</span>
          )}
        </div>
        <span>
          Smoothness: Table 2399-4 ({eq.label}) | ALR: Table 2399-7
        </span>
      </div>
    </div>
  );
}